import { useEffect } from 'react';
import { AlertTriangle, Info, X } from 'lucide-react';

/**
 * Une fenêtre au centre de l'écran, pour ce qui interrompt.
 *
 * Les refus s'affichaient jusqu'ici en bandeau, à l'endroit où l'on avait
 * cliqué — c'est-à-dire parfois hors du champ de vision, sur une grille de
 * planning qui défile. On recliquait alors le même bouton en croyant que
 * rien ne s'était passé, et le navigateur enregistrait un second échec.
 *
 * Une fenêtre centrale règle les deux : elle se voit, et elle empêche de
 * refaire le geste avant d'avoir tranché.
 *
 * Elle sert à deux choses qui se ressemblent assez pour partager le même
 * cadre : dire pourquoi c'est refusé, et demander s'il faut passer outre. La
 * seconde n'apparaît que si une action de confirmation est fournie — un
 * simple message n'a pas à proposer de bouton dangereux.
 */
export default function Dialogue({
  titre,
  message,
  precision,
  ton = 'alerte',
  libelleConfirmer,
  onConfirmer,
  onFermer,
}: {
  titre: string;
  message: string;
  /** Ce qu'il faut savoir avant de trancher, sous le message. */
  precision?: string;
  ton?: 'alerte' | 'information';
  libelleConfirmer?: string;
  onConfirmer?: () => void;
  onFermer: () => void;
}) {
  // Échap ferme : c'est le réflexe, et une fenêtre qui ne s'y plie pas donne
  // l'impression d'être bloqué.
  useEffect(() => {
    const auClavier = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFermer();
    };
    window.addEventListener('keydown', auClavier);
    return () => window.removeEventListener('keydown', auClavier);
  }, [onFermer]);

  const alerte = ton === 'alerte';
  const Icone = alerte ? AlertTriangle : Info;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={titre}
      className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      // Le clic sur le fond ferme, mais pas celui sur la fenêtre elle-même.
      onClick={(e) => {
        if (e.target === e.currentTarget) onFermer();
      }}
    >
      <div className="bg-white dark:bg-gray-900 w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-xl">
        <div className="p-5 space-y-3">
          <div className="flex items-start gap-3">
            <span
              className={`w-9 h-9 rounded-full grid place-items-center shrink-0 ${
                alerte
                  ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                  : 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
              }`}
            >
              <Icone className="w-5 h-5" />
            </span>
            <div className="min-w-0 flex-1">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100">{titre}</h3>
              <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">{message}</p>
              {precision && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">{precision}</p>
              )}
            </div>
            <button
              type="button"
              onClick={onFermer}
              aria-label="Fermer"
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 shrink-0"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="flex flex-wrap gap-2 justify-end pt-1">
            <button type="button" className="btn-outline text-sm" onClick={onFermer}>
              {onConfirmer ? 'Annuler' : "J'ai compris"}
            </button>
            {onConfirmer && (
              <button
                type="button"
                onClick={onConfirmer}
                className="text-sm px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-medium"
              >
                {libelleConfirmer ?? 'Continuer'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
